import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Box, IconButton, Popover, Tooltip, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { getSVG } from '../../utils/getSvg'

/**
 * A narrow header's page actions (Export PDF, Reset, Import…): one "more" button instead of a row
 * of labelled buttons that wrap and squeeze the logo. The page's own buttons open in a column
 * under it, unchanged; pressing any of them closes the list.
 */
const HeaderOverflowActions = ({ label, children }) => {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  return (
    <>
      <Tooltip title={label}>
        <IconButton
          aria-label={label}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          data-testid="header-overflow-actions"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            flexShrink: 0,
            width: 40,
            height: 40,
            borderRadius: '10px',
            backgroundColor: open ? alpha(theme.palette.text.primary, 0.06) : 'transparent',
            '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.06) },
          }}
        >
          {getSVG('three-dots', theme.palette.text.secondary, { width: 4, height: 18 })}
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { borderRadius: 2, minWidth: 180, maxWidth: 'calc(100vw - 32px)' } }}
      >
        <Box
          onClick={() => setAnchorEl(null)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 1,
            p: 1.5,
            // Pages space their header buttons sideways; stacked, the gap does it.
            '& .MuiButton-root': { m: 0, justifyContent: 'flex-start', whiteSpace: 'nowrap' },
            // A page that wraps its buttons in a row box gets the same column.
            '& > div': { flexDirection: 'column', alignItems: 'stretch' },
          }}
        >
          {children}
        </Box>
      </Popover>
    </>
  )
}

HeaderOverflowActions.propTypes = {
  label: PropTypes.string,
  children: PropTypes.node,
}

HeaderOverflowActions.defaultProps = {
  label: '',
}

export default HeaderOverflowActions
